import { createDistanceSurcharge } from "@/api/settings";
import CloseIcon from "@mui/icons-material/Close";
import {
    Box,
    Dialog,
    DialogContent,
    DialogTitle,
    IconButton,
    InputAdornment,
    Stack,
    Toolbar,
    Typography,
} from "@mui/material";
import { FormButton, FormContainer, FormTextFieldElement } from "@rhf-kit/mui";
import { useMutation } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { enqueueSnackbar } from "notistack";

interface Props {
    open: boolean;
    setOpen: (open: boolean) => void;
    mutateDistanceSurcharges: () => void;
}

interface FormData {
    minimumMiles: number;
    surcharge: number;
}

export const CreateDistanceSurchargeModal = ({
    open,
    setOpen,
    mutateDistanceSurcharges,
}: Props) => {
    const createDistanceSurchargeMutation = useMutation({
        mutationFn: createDistanceSurcharge,
        onSuccess: () => {
            mutateDistanceSurcharges();
            enqueueSnackbar(`Distance surcharge created successfully`, {
                variant: "success",
            });
            setOpen(false);
        },
        onError: (error: AxiosError<{ detail: string }>) => {
            if (
                error.response &&
                error.response.data &&
                error.response.data.detail
            ) {
                enqueueSnackbar(error.response.data.detail, {
                    variant: "error",
                });
            }
        },
    });

    const handleSubmit = (data: FormData) => {
        createDistanceSurchargeMutation.mutate({
            minimum_miles: data.minimumMiles,
            surcharge: data.surcharge * 100,
        });
    };

    return (
        <Dialog
            fullWidth
            maxWidth="sm"
            open={open}
            onClose={() => setOpen(false)}
        >
            <DialogTitle>
                <Toolbar variant="dense">
                    <Typography variant="h6" fontWeight={600}>
                        Create a new distance surcharge
                    </Typography>
                    <Box flexGrow={1} />
                    <IconButton edge="end" onClick={() => setOpen(false)}>
                        <CloseIcon />
                    </IconButton>
                </Toolbar>
            </DialogTitle>
            <DialogContent>
                <FormContainer
                    defaultValues={{ minimumMiles: 0, surcharge: 0 }}
                    onSuccess={handleSubmit}
                >
                    <Stack direction={"column"} spacing={2} my={2}>
                        <FormTextFieldElement
                            id="minimumMiles"
                            name="minimumMiles"
                            label="Minimum Miles"
                            type="number"
                            size="small"
                            required
                            rules={{
                                min: {
                                    value: 1,
                                    message: "Minimum Miles must be at least 1",
                                },
                            }}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment
                                        position="end"
                                        variant="standard"
                                    >
                                        miles
                                    </InputAdornment>
                                ),
                            }}
                        />
                        <FormTextFieldElement
                            id="surcharge"
                            name="surcharge"
                            label="Surcharge"
                            type="number"
                            rules={{
                                min: {
                                    value: 0.01,
                                    message: "Surcharge must be at least $0.01",
                                },
                            }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment
                                        position="start"
                                        variant="standard"
                                    >
                                        $
                                    </InputAdornment>
                                ),
                            }}
                            size="small"
                            required
                        />
                    </Stack>
                    <FormButton variant="contained" color="primary">
                        Create Distance Surcharge
                    </FormButton>
                </FormContainer>
            </DialogContent>
        </Dialog>
    );
};
